import PeacockDataTable from '@/components/organisms/PeacockDataTable';
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper';
import {useRewardCatalog} from '@/hooks/useRewardCatalog';
import {usePeacockDataTableConfig} from '@/hooks/usePeacockDataTableConfig';
import {useEffect, useMemo, useState, useCallback} from 'react';
import DownloadDigitalCodeReportModal from './DownloadDigitalCodeReportModal';
import {FileUploadWithModal} from '@/components/organisms';
import {useNavigate} from 'react-router-dom';
import {cellDataMapperObj} from './cellDataMapper';
import {
  REWARD_CATALOG_ROUTES,
  Constants,
  DISABLE_HEADERS,
  UPLOAD_TITLES,
  UPLOAD_ERROR_MESSAGES
} from '@/utils/constants/commonConstants';
import {convertToLowerCase} from '@/utils/commons';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useAppDispatch} from '@/store';
import {resetRewardsData} from '@/store/slices/rewardsSlice';
import PropTypes from 'prop-types';

export const RewardCatalogList = ({rewardIdFilter}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [uploadFor, setUploadFor] = useState('');
  const [showDigitalCodeReportModal, setShowDigitalCodeReportModal] =
    useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectionMeta, setSelectionMeta] = useState({
    isAllSelected: false,
    excludeIds: []
  });

  const {
    rewardCatalogData,
    rewardCatalogColConfigData,
    totalRecords,
    pageIndex,
    pageSize,
    setPageSize,
    customRules,
    customRulesRewards,
    uploading,
    uploadProgress,
    uploadErrorMessage,
    formData,
    setFormData,
    openUpload,
    setOpenUpload = () => {},
    setPageIndex,
    setSortingObj,
    sortingObj,
    setSearchArry,
    setSearchArrImmediate,
    onDownloadBtnClick,
    isLoading,
    isFetching,
    handleResetAll,
    dropdownList,
    handleDownloadTemplate,
    handleBulkUpload,
    setUploading,
    handleCloseNotification,
    handleCloseModal,
    downloadRewardReport
  } = useRewardCatalog();

  const normalizedRewardIds = useMemo(() => {
    if (!Array.isArray(rewardIdFilter) || rewardIdFilter.length === 0) {
      return [];
    }
    return rewardIdFilter
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }, [rewardIdFilter]);

  useEffect(() => {
    if (normalizedRewardIds.length === 0) return;

    // Replace any existing filters (explicit requirement):
    // reset table filters and then apply rewardIdList.
    handleResetAll();
    setPageIndex(0);

    setSearchArrImmediate({
      field: 'rewardIdList',
      value: JSON.stringify(normalizedRewardIds),
      type: 'array'
    });
  }, [
    normalizedRewardIds,
    handleResetAll,
    setPageIndex,
    setSearchArrImmediate
  ]);

  const handleNavigation = (data) => {
    // Reseting the state before moving to the screen
    dispatch(resetRewardsData());
    navigate(data);
  };

  const columns = useMemo(
    () =>
      getCellDataMappedCols(
        cellDataMapperObj(handleNavigation, downloadRewardReport),
        rewardCatalogColConfigData
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, downloadRewardReport, rewardCatalogColConfigData]
  );

  const {
    records,
    handleDownload,
    paginationConfig,
    sortingConfig,
    filterConfig,
    selectionConfig
  } = usePeacockDataTableConfig({
    data: rewardCatalogData,
    pageIndex,
    setPageIndex,
    setPageSize,
    setSortingObj,
    sortingObj,
    setSearchArry,
    handleResetAll,
    onDownloadBtnClick,
    disabledHeaders: DISABLE_HEADERS.REWARD_CATALOG_HEADERS,
    onRowSelect: (rows, meta) => {
      setSelectedRows(rows);
      if (meta) {
        setSelectionMeta(meta);
      }
    }
  });

  const handleActionClick = useCallback(
    (action) => {
      const displayText = convertToLowerCase(action?.displayText);
      switch (displayText) {
        case REWARD_CATALOG_ROUTES.NEW_REWARD:
          dispatch(resetRewardsData());
          navigate(ROUTES.REWARDS_CREATE);
          break;
        case REWARD_CATALOG_ROUTES.BULK_UPLOAD_REWARDS:
          setUploadFor(Constants.BulkRewards);
          setOpenUpload(true);
          break;
        case REWARD_CATALOG_ROUTES.BULK_UPLOAD_DIGITAL_CODES:
          setUploadFor(Constants.BulkDigitalCodes);
          setOpenUpload(true);
          break;
        case REWARD_CATALOG_ROUTES.DOWNLOAD_DIGITAL_CODE_REPORT:
          setShowDigitalCodeReportModal(true);
          break;
        case REWARD_CATALOG_ROUTES.DOWNLOAD:
          handleDownload(selectedRows, selectionMeta);
          break;
        default:
          break;
      }
    },
    [
      dispatch,
      navigate,
      setOpenUpload,
      handleDownload,
      selectedRows,
      selectionMeta
    ]
  );

  const toolbarConfig = useMemo(
    () => ({
      showDownload: false,
      onDownload: handleDownload,
      showActionMenu: true,
      actionItems: dropdownList,
      onActionClick: handleActionClick
    }),
    [handleDownload, dropdownList, handleActionClick]
  );

  return (
    <div className='rewardCatalogDataTable relative w-full'>
      <PeacockDataTable
        columns={columns}
        records={records}
        isLoading={isLoading}
        isFetching={isFetching}
        pageSize={pageSize}
        totalRecords={totalRecords}
        paginationConfig={paginationConfig}
        selectionConfig={selectionConfig}
        sortingConfig={sortingConfig}
        filterConfig={filterConfig}
        toolbarConfig={toolbarConfig}
        displayConfig={{wrapperClassName: 'reward-catalog-datatable'}}
      />
      {totalRecords > 0 && (
        <div className='text-md py-2 pr-4 text-right text-[color:var(--color-text-primary)]'>
          {totalRecords} total items
        </div>
      )}
      <DownloadDigitalCodeReportModal
        showModal={showDigitalCodeReportModal}
        onClose={() => setShowDigitalCodeReportModal(false)}
      />
      <FileUploadWithModal
        title={
          uploadFor === Constants.BulkDigitalCodes
            ? UPLOAD_TITLES.UPLOAD_DIGITAL_CODES_LIST
            : UPLOAD_TITLES.UPLOAD_REWARDS_LIST
        }
        fileType='csv'
        uploadFor={uploadFor}
        openUpload={openUpload}
        uploading={uploading}
        setUploading={setUploading}
        uploadProgress={uploadProgress}
        uploadErrorMessage={uploadErrorMessage}
        setOpenUpload={setOpenUpload}
        setFormData={setFormData}
        formData={formData}
        customErrorMessages={{
          wrongFormat: UPLOAD_ERROR_MESSAGES.WRONG_FORMAT
        }}
        showDownloadTemplate={true}
        handleDownloadTemplate={handleDownloadTemplate}
        customRules={
          uploadFor === Constants.BulkDigitalCodes
            ? customRules
            : customRulesRewards
        }
        isCsvValidationRequired={true}
        primaryButtonText='Upload'
        subTitle=' '
        isPending={false}
        onClickPrimary={() => {
          handleBulkUpload(formData, uploadFor);
        }}
        primaryButtonDisabled={!formData?.storeList}
        handleCloseNotification={handleCloseNotification}
        handleCloseModal={handleCloseModal}
      />
    </div>
  );
};

RewardCatalogList.propTypes = {
  rewardIdFilter: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  )
};
